// Reports (staff): standard and saved reports, run with their questions (organizations, dates), previewed on screen,
// customised (columns, filters, grouping and totals, sort) and exported to Excel or a branded PDF.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { isAdmin, post, patch } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { orgMatches } from '../lib/orgSearch.ts';
import { useData } from '../lib/useData.ts';

// ------------------------------------------------------------------ types shared with the API
export type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'org';
export interface CatalogField { key: string; label: string; group: string; type: FieldType; options?: Record<string, string>; description?: string; decimals?: number }
export interface Catalog { fields: CatalogField[]; ops: string[]; aggregates: string[] }
export interface Filter { field: string; op: string; value?: unknown; value2?: unknown; ask?: boolean; label?: string }
export type Dataset = 'assessments' | 'labs';
export const DATASET_LABEL: Record<Dataset, string> = { assessments: 'Assessments (one row per visit)', labs: 'Lab results (one row per lab draw)' };
export interface Spec {
  dataset: Dataset; columns: string[]; filters: Filter[]; groupBy: string[]; groupRows?: { field: string } | null;
  measures: { fn: string; field?: string; label?: string }[]; sort: { key: string; dir: 'asc' | 'desc' }[]; limit?: number;
}
interface OutColumn { key: string; label: string; type: FieldType | 'count'; decimals?: number }
interface Section { value: string | null; label: string; start: number; end: number; count: number; averages: Record<string, number | null> }
interface Result {
  columns: OutColumn[]; rows: Record<string, unknown>[]; total: number; truncated: boolean; filters: string[];
  sections?: Section[]; overall?: { count: number; averages: Record<string, number | null> }; groupLabel?: string;
}
type Catalogs = Record<Dataset, Catalog>;
interface ReportList {
  standard: { key: string; name: string; description: string; spec: Spec }[];
  saved: { id: string; name: string; description: string | null; shared: boolean; mine: boolean; ownerName: string }[];
}

export const OP_LABEL: Record<string, string> = {
  eq: 'is', neq: 'is not', in: 'is any of', not_in: 'is none of', contains: 'contains', starts_with: 'starts with',
  gt: 'more than', gte: 'at least', lt: 'less than', lte: 'at most', between: 'between',
  is_true: 'is yes', is_false: 'is no', is_empty: 'is blank', not_empty: 'is filled in',
  last_days: 'in the last … days', this_year: 'this year', last_year: 'last year', this_month: 'this month', last_month: 'last month', year_to_date: 'year to date',
};
export const OPS_FOR: Record<FieldType, string[]> = {
  text: ['eq', 'neq', 'contains', 'starts_with', 'in', 'is_empty', 'not_empty'],
  org: ['in', 'not_in', 'contains', 'is_empty', 'not_empty'],
  enum: ['in', 'not_in', 'eq', 'neq', 'is_empty', 'not_empty'],
  number: ['gte', 'lte', 'between', 'eq', 'neq', 'gt', 'lt', 'is_empty', 'not_empty'],
  date: ['between', 'this_year', 'last_year', 'year_to_date', 'this_month', 'last_month', 'last_days', 'gte', 'lte', 'eq', 'is_empty', 'not_empty'],
  boolean: ['is_true', 'is_false', 'is_empty', 'not_empty'],
};
const NO_VALUE = new Set(['is_true', 'is_false', 'is_empty', 'not_empty', 'this_year', 'last_year', 'this_month', 'last_month', 'year_to_date']);
const AGG_LABEL: Record<string, string> = { count: 'Number of assessments', count_people: 'Number of people', avg: 'Average', min: 'Lowest', max: 'Highest', sum: 'Total' };

export const blankSpec = (dataset: Dataset = 'assessments'): Spec => dataset === 'labs'
  ? { dataset, columns: ['person_name', 'org', 'performed_on', 'lab_test'], filters: [{ field: 'performed_on', op: 'this_year' }], groupBy: [], measures: [], sort: [] }
  : { dataset, columns: ['person_name', 'org', 'assessed_on'], filters: [{ field: 'assessed_on', op: 'this_year' }], groupBy: [], measures: [], sort: [] };

// ------------------------------------------------------------------ formatting
export function fmtCell(c: OutColumn, v: unknown, f?: CatalogField): string {
  if (v == null || v === '') return '';
  if (c.type === 'boolean') return v ? 'Yes' : 'No';
  if (c.type === 'date' && typeof v === 'string') { const [y, m, d] = v.split('-'); return `${Number(m)}/${Number(d)}/${y}`; }
  if (c.type === 'number' || c.type === 'count') { const n = Number(v); return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: c.decimals ?? 2, minimumFractionDigits: c.type === 'count' ? 0 : Math.min(c.decimals ?? 0, 2) }) : String(v); }
  if (f?.options && typeof v === 'string') return f.options[v] ?? v;
  return String(v);
}
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

async function download(url: string, body: unknown, fallbackName: string) {
  const res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) { const j = await res.json().catch(() => null) as { error?: string } | null; throw new Error(j?.error ?? `Export failed (${res.status})`); }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(await res.blob()); a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

// ------------------------------------------------------------------ the Reports tab
export function Reports() {
  const { kind, id } = useParams();
  const list = useData<ReportList>('/api/reports');
  const ca = useData<Catalog>('/api/reports/catalog?dataset=assessments');
  const cl = useData<Catalog>('/api/reports/catalog?dataset=labs');
  const catalogs: Catalogs | null = ca.data && cl.data ? { assessments: ca.data, labs: cl.data } : null;
  const nav = useNavigate();
  const std = kind === 'standard' ? list.data?.standard.find((s) => s.key === id) : undefined;
  const saved = useData<{ id: string; name: string; description: string | null; spec: Spec; shared: boolean; mine: boolean; ownerName: string }>(
    kind === 'saved' && id ? `/api/reports/saved/${id}` : null);
  if (list.error) return <ErrorBox error={list.error} />;
  const mine = list.data?.saved.filter((s) => s.mine) ?? [];
  const shared = list.data?.saved.filter((s) => !s.mine) ?? [];
  return (
    <div className="reports">
      <aside className="reports-list card">
        <div className="spread"><h2 style={{ margin: 0 }}>Reports</h2><button className="small" onClick={() => nav('/reports/new')}>New report</button></div>
        <h3>Standard</h3>
        <ul>{list.data?.standard.map((s) => <li key={s.key}><Link className={kind === 'standard' && id === s.key ? 'active' : ''} to={`/reports/standard/${s.key}`}>{s.name}</Link></li>)}</ul>
        <h3>My reports</h3>
        {mine.length ? <ul>{mine.map((s) => <li key={s.id}><Link className={id === s.id ? 'active' : ''} to={`/reports/saved/${s.id}`}>{s.name}</Link>{s.shared && <span className="muted small"> · shared</span>}</li>)}</ul>
          : <p className="muted small">None yet. Customize a report and save it.</p>}
        {shared.length > 0 && <><h3>Shared with everyone</h3>
          <ul>{shared.map((s) => <li key={s.id}><Link className={id === s.id ? 'active' : ''} to={`/reports/saved/${s.id}`}>{s.name}</Link><span className="muted small"> · {s.ownerName}</span></li>)}</ul></>}
      </aside>
      <section className="reports-main">
        {!catalogs ? <p className="muted">Loading…</p>
          : kind === 'standard' && std ? <ReportRunner key={`s-${std.key}`} catalogs={catalogs} title={std.name} description={std.description} initial={std.spec} standardKey={std.key} onSaved={(nid) => { void list.reload(); nav(`/reports/saved/${nid}`); }} />
          : kind === 'saved' && saved.data ? <ReportRunner key={`r-${saved.data.id}`} catalogs={catalogs} title={saved.data.name} description={saved.data.description ?? ''} initial={saved.data.spec}
              saved={saved.data} onSaved={(nid) => { void list.reload(); void saved.reload(); nav(`/reports/saved/${nid}`); }} onRemoved={() => { void list.reload(); nav('/reports'); }} />
          : kind === 'new' ? <ReportRunner key="new" catalogs={catalogs} title="New report" description="" initial={blankSpec()} startEditing onSaved={(nid) => { void list.reload(); nav(`/reports/saved/${nid}`); }} />
          : kind === 'saved' && saved.error ? <ErrorBox error={saved.error} />
          : <div className="card"><h1 style={{ marginTop: 0 }}>Reports</h1>
              <p>Choose a report on the left. Standard reports ask for what they need (organizations, dates) and can be customized and saved as your own.
                <b> New report</b> starts from scratch.</p>
              <p className="muted small">Every report downloads as Excel or a PDF on NGF letterhead. Reports cover assessments that are not voided or replaced by a correction, or lab results (heavy metals, LabCorp).</p></div>}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ running one report
function ReportRunner({ catalogs, title, description, initial, standardKey, saved, startEditing, onSaved, onRemoved }: {
  catalogs: Catalogs; title: string; description: string; initial: Spec; standardKey?: string; startEditing?: boolean;
  saved?: { id: string; name: string; shared: boolean; mine: boolean }; onSaved: (id: string) => void; onRemoved?: () => void;
}) {
  const { me } = useAuth();
  const [spec, setSpec] = useState<Spec>(initial);
  const [name, setName] = useState(title);
  const [aiOpen, setAiOpen] = useState(!!startEditing);
  const [answers, setAnswers] = useState<Record<string, { value?: unknown; value2?: unknown }>>(() => defaultAnswers(initial));
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [pdf, setPdf] = useState<{ id: string; status: string } | null>(null);
  const catalog = catalogs[spec.dataset] ?? catalogs.assessments;
  const fieldOf = useMemo(() => new Map(catalog.fields.map((f) => [f.key, f])), [catalog]);
  const changed = JSON.stringify(spec) !== JSON.stringify(initial);
  const asked = spec.filters.map((f, i) => ({ f, i })).filter((x) => x.f.ask);

  const run = async (s: Spec = spec) => {
    setErr(null); setBusy('run');
    try { setResult(await post<Result>('/api/reports/run', { spec: s, answers, title: name })); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  useEffect(() => { if (!asked.length && !startEditing) void run(); }, []);   // a new report waits for the AI or Customize   // eslint-disable-line react-hooks/exhaustive-deps

  const excel = async () => {
    setErr(null); setBusy('xlsx');
    try { await download('/api/reports/export.xlsx', { spec, answers, title: name }, `${name}.xlsx`); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  const makePdf = async () => {
    setErr(null); setBusy('pdf');
    try {
      const r = await post<{ id: string }>('/api/reports/pdf', { spec, answers, title: name, reportId: saved?.id, standardKey });
      setPdf({ id: r.id, status: 'queued' });
      for (let i = 0; i < 90; i++) {
        await new Promise((ok) => setTimeout(ok, 2000));
        const s = await fetch(`/api/reports/runs/${r.id}`, { credentials: 'same-origin' }).then((x) => x.json()) as { status: string; error?: string };
        setPdf({ id: r.id, status: s.status });
        if (s.status === 'done') { window.location.href = `/api/reports/runs/${r.id}/file`; break; }
        if (s.status === 'failed') throw new Error(`The PDF could not be made: ${s.error ?? 'error'}`);
      }
    } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  const saveAs = async () => {
    const n = window.prompt('Save this report as (your reports):', saved || standardKey ? `${name} (custom)` : name === 'New report' ? 'My report' : name);
    if (!n?.trim()) return;
    setErr(null);
    try { const r = await post<{ id: string }>('/api/reports/saved', { name: n.trim(), spec }); onSaved(r.id); } catch (e) { setErr(e); }
  };
  const saveChanges = async () => {
    setErr(null);
    try { await patch(`/api/reports/saved/${saved!.id}`, { spec }); onSaved(saved!.id); } catch (e) { setErr(e); }
  };
  const share = async (on: boolean) => {
    setErr(null);
    try { await patch(`/api/reports/saved/${saved!.id}`, { shared: on }); onSaved(saved!.id); } catch (e) { setErr(e); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${title}" from the report list?`)) return;
    try { await post(`/api/reports/saved/${saved!.id}/archive`); onRemoved?.(); } catch (e) { setErr(e); }
  };
  const canChange = !!saved && (saved.mine || (me && isAdmin(me.role)));

  return (
    <>
      <div className="card">
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div><h1 style={{ margin: 0 }}>{name}</h1>{description && <p className="muted" style={{ margin: '0.25rem 0 0' }}>{description}</p>}</div>
          <div className="actions">
            <button className={aiOpen ? '' : 'secondary'} onClick={() => setAiOpen(!aiOpen)}>{aiOpen ? 'Hide AI builder' : 'Build with AI'}</button>
            <button className="secondary" onClick={() => setEditing(!editing)}>{editing ? 'Hide customize' : 'Customize'}</button>
            {saved && canChange && changed && <button onClick={() => void saveChanges()}>Save changes</button>}
            <button className="secondary" onClick={() => void saveAs()}>Save as…</button>
            {saved && me && isAdmin(me.role) && <button className="secondary" onClick={() => void share(!saved.shared)}>{saved.shared ? 'Stop sharing' : 'Share with all staff'}</button>}
            {saved && canChange && <button className="secondary danger" onClick={() => void remove()}>Remove</button>}
          </div>
        </div>
        {asked.length > 0 && (
          <div className="report-questions">
            {asked.map(({ f, i }) => (
              <Question key={i} filter={f} field={fieldOf.get(f.field)} answer={answers[String(i)] ?? {}}
                onChange={(a) => setAnswers((cur) => ({ ...cur, [String(i)]: a }))} />))}
          </div>)}
        <div className="actions" style={{ marginTop: '0.75rem' }}>
          <button onClick={() => void run()} disabled={!!busy}>{busy === 'run' ? 'Running…' : 'Run report'}</button>
          <button className="secondary" onClick={() => void excel()} disabled={!!busy}>{busy === 'xlsx' ? 'Preparing…' : 'Excel'}</button>
          <button className="secondary" onClick={() => void makePdf()} disabled={!!busy}>{busy === 'pdf' ? (pdf?.status === 'running' ? 'Making the PDF…' : 'Queued…') : 'PDF'}</button>
        </div>
        <ErrorBox error={err} />
      </div>

      {aiOpen && <AiBuilder spec={spec} lastRows={result?.total ?? null} fresh={!!startEditing}
        onSpec={(s, t) => { setSpec(s); setAnswers((a) => ({ ...defaultAnswers(s), ...a })); if (t && (!saved && !standardKey)) setName(t); void run(s); }} />}

      {editing && <SpecEditor catalogs={catalogs} spec={spec} onChange={(s) => { setSpec(s); setAnswers((a) => ({ ...defaultAnswers(s), ...a })); }} />}

      {result && (
        <div className="card">
          <div className="spread"><b>{result.total.toLocaleString('en-US')} row{result.total === 1 ? '' : 's'}</b>
            <span className="muted small">{result.truncated ? `showing the first ${result.rows.length.toLocaleString('en-US')}; exports include up to 50,000` : ''}</span></div>
          {result.filters.length > 0 && <p className="muted small" style={{ margin: '0.2rem 0 0.5rem' }}>{result.filters.join(' · ')}</p>}
          {!result.rows.length ? <p className="muted">No rows match.</p> : (
            <div className="report-table-wrap"><ResultTable result={result} fieldOf={fieldOf} className="report-table" /></div>)}
        </div>)}
    </>
  );
}

/** The rows of a report; with sections (groupRows), a heading per group, its rows, and an average row after each, then overall. */
function ResultTable({ result, fieldOf, className }: { result: Result; fieldOf: Map<string, CatalogField>; className: string }) {
  const cls = (c: OutColumn) => (c.type === 'number' || c.type === 'count' ? 'num' : '');
  const row = (r: Record<string, unknown>, n: number) => <tr key={n}>{result.columns.map((c) => <td key={c.key} className={cls(c)}>{fmtCell(c, r[c.key], fieldOf.get(c.key))}</td>)}</tr>;
  const avg = (label: string, count: number, a: Record<string, number | null>, k: string) => (
    <tr key={k} className="report-avg">{result.columns.map((c, i) => (
      <td key={c.key} className={cls(c)}>{i === 0 ? `${label} (${count})` : c.type === 'number' && a[c.key] != null ? fmtCell({ ...c, decimals: Math.max(1, c.decimals ?? 1) }, a[c.key]) : ''}</td>))}</tr>);
  return (
    <table className={className}><thead><tr>{result.columns.map((c) => <th key={c.key} className={cls(c)}>{c.label}</th>)}</tr></thead>
      {result.sections ? result.sections.map((sc, si) => (
        <tbody key={si}>
          <tr className="report-section"><td colSpan={result.columns.length}>{result.groupLabel ? `${result.groupLabel}: ` : ''}{sc.label}</td></tr>
          {result.rows.slice(sc.start, sc.end).map((r, n) => row(r, sc.start + n))}
          {avg('Average', sc.count, sc.averages, `a${si}`)}
        </tbody>))
        : <tbody>{result.rows.map(row)}</tbody>}
      {result.sections && result.overall && <tbody>{avg('Overall average', result.overall.count, result.overall.averages, 'overall')}</tbody>}
    </table>);
}

/** Default answers for asked filters: dates this year to today. */
function defaultAnswers(spec: Spec): Record<string, { value?: unknown; value2?: unknown }> {
  const out: Record<string, { value?: unknown; value2?: unknown }> = {};
  spec.filters.forEach((f, i) => {
    if (!f.ask) return;
    if (f.op === 'between' && f.value == null && f.value2 == null) out[String(i)] = { value: `${new Date().getFullYear()}-01-01`, value2: today() };
    else out[String(i)] = { value: f.value, value2: f.value2 };
  });
  return out;
}

/** One asked filter: organizations (searchable list), a date range (with quick choices), or a value. */
function Question({ filter, field, answer, onChange }: { filter: Filter; field?: CatalogField; answer: { value?: unknown; value2?: unknown }; onChange: (a: { value?: unknown; value2?: unknown }) => void }) {
  const label = filter.label ?? field?.label ?? filter.field;
  if (field?.type === 'org' && (filter.op === 'in' || filter.op === 'not_in')) {
    return <div className="field report-q"><label>{label}</label><OrgPicker value={(answer.value as string[] | undefined) ?? []} onChange={(v) => onChange({ value: v })} /></div>;
  }
  if (field?.type === 'date' && filter.op === 'between') {
    const y = new Date().getFullYear();
    const set = (a: string, b: string) => onChange({ value: a, value2: b });
    const last12 = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
    return (
      <div className="field report-q"><label>{label}</label>
        <div className="row" style={{ gap: '0.4rem', alignItems: 'center' }}>
          <input type="date" value={String(answer.value ?? '')} onChange={(e) => onChange({ ...answer, value: e.target.value })} style={{ maxWidth: 170 }} />
          <span className="muted">to</span>
          <input type="date" value={String(answer.value2 ?? '')} onChange={(e) => onChange({ ...answer, value2: e.target.value })} style={{ maxWidth: 170 }} />
        </div>
        <div className="chips small">
          <button type="button" className="link" onClick={() => set(`${y}-01-01`, today())}>This year</button>
          <button type="button" className="link" onClick={() => set(`${y - 1}-01-01`, `${y - 1}-12-31`)}>Last year</button>
          <button type="button" className="link" onClick={() => set(last12(), today())}>Last 12 months</button>
          <button type="button" className="link" onClick={() => set('', '')}>Any date</button>
        </div>
      </div>);
  }
  return <div className="field report-q"><label>{label} <span className="muted small">({OP_LABEL[filter.op] ?? filter.op})</span></label>
    <ValueInput field={field} op={filter.op} value={answer.value} value2={answer.value2} onChange={(v, v2) => onChange({ value: v, value2: v2 })} /></div>;
}

interface OrgOpt { id: string; name: string }
/** Choose organizations: a search box and the matching ones to tick; the chosen ones as chips. */
export function OrgPicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const orgs = useData<OrgOpt[]>('/api/departments');
  const [q, setQ] = useState('');
  const byId = useMemo(() => new Map((orgs.data ?? []).map((o) => [o.id, o])), [orgs.data]);
  const hits = q.trim() ? (orgs.data ?? []).filter((o) => orgMatches(o.name, q) && !value.includes(o.id)).slice(0, 8) : [];
  return (
    <div className="org-picker">
      <div className="chips">{value.map((id) => (
        <span key={id} className="chip">{byId.get(id)?.name ?? 'Organization'} <button type="button" aria-label="Remove" onClick={() => onChange(value.filter((x) => x !== id))}>×</button></span>))}
        {!value.length && <span className="muted small">All organizations (type to choose)</span>}</div>
      <input type="search" placeholder="Type an organization name…" value={q} onChange={(e) => setQ(e.target.value)} />
      {hits.length > 0 && <ul className="org-picker-hits">{hits.map((o) => (
        <li key={o.id}><button type="button" onClick={() => { onChange([...value, o.id]); setQ(''); }}>{o.name}</button></li>))}</ul>}
    </div>
  );
}

function ValueInput({ field, op, value, value2, onChange }: { field?: CatalogField; op: string; value: unknown; value2?: unknown; onChange: (v: unknown, v2?: unknown) => void }) {
  if (NO_VALUE.has(op)) return null;
  if (field?.type === 'org' && (op === 'in' || op === 'not_in')) return <OrgPicker value={(value as string[] | undefined) ?? []} onChange={(v) => onChange(v)} />;
  if (field?.type === 'enum' && field.options) {
    if (op === 'in' || op === 'not_in') {
      const cur = new Set((value as string[] | undefined) ?? []);
      return <div className="chips">{Object.entries(field.options).map(([k, l]) => (
        <label key={k} className="small"><input type="checkbox" checked={cur.has(k)} onChange={(e) => { const n = new Set(cur); if (e.target.checked) n.add(k); else n.delete(k); onChange([...n]); }} />{l}</label>))}</div>;
    }
    return <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}><option value="">—</option>{Object.entries(field.options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>;
  }
  const type = field?.type === 'date' && op !== 'last_days' ? 'date' : field?.type === 'number' || op === 'last_days' ? 'number' : 'text';
  const conv = (s: string) => (type === 'number' ? (s === '' ? null : Number(s)) : s);
  if (op === 'in') return <input value={Array.isArray(value) ? value.join(', ') : String(value ?? '')} placeholder="values, separated by commas" onChange={(e) => onChange(e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />;
  if (op === 'between') return <div className="row" style={{ gap: '0.4rem', alignItems: 'center' }}>
    <input type={type} value={String(value ?? '')} onChange={(e) => onChange(conv(e.target.value), value2)} /><span className="muted">to</span>
    <input type={type} value={String(value2 ?? '')} onChange={(e) => onChange(value, conv(e.target.value))} /></div>;
  return <input type={type} value={String(value ?? '')} onChange={(e) => onChange(conv(e.target.value))} />;
}

// ------------------------------------------------------------------ AI builder
interface ChatTurn { role: 'user' | 'assistant'; content: string }

/** Chat with Claude to build or change the report. It sees the report fields and the current definition (not the rows),
 *  asks when something is unclear, and changes the report, which then runs. */
function AiBuilder({ spec, lastRows, fresh, onSpec }: { spec: Spec; lastRows: number | null; fresh: boolean; onSpec: (s: Spec, title: string | null) => void }) {
  const [history, setHistory] = useState<ChatTurn[]>(() => [{ role: 'assistant', content: fresh
    ? 'What would you like to see? For example: "everyone referred this year at fire departments, with their blood pressure and risk class", or "how many COHSG screenings each organization had per month in 2026".'
    : 'Tell me how to change this report: add or remove columns, filter (organizations, dates, results), group and total, or sort.' }]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [history, busy]);
  const send = async () => {
    const message = text.trim();
    if (!message || busy) return;
    setErr(null); setBusy(true); setText('');
    const prior = history.slice(1);   // the greeting is not part of the conversation
    setHistory((h) => [...h, { role: 'user', content: message }]);
    try {
      const r = await post<{ reply: string; spec: Spec | null; title: string | null }>('/api/reports/ai', { message, history: prior, spec, lastRows });
      setHistory((h) => [...h, { role: 'assistant', content: r.reply }]);
      if (r.spec) onSpec(r.spec, r.title);
    } catch (e) { setErr(e); setText(message); setHistory((h) => h.slice(0, -1)); } finally { setBusy(false); }
  };
  return (
    <div className="card ai-builder">
      <div className="spread"><h2 style={{ margin: 0 }}>AI report builder</h2><span className="muted small">Claude sees the report's fields and settings, never patients' data.</span></div>
      <div className="ai-chat">
        {history.map((t, i) => <div key={i} className={`ai-msg ${t.role}`}>{t.content}</div>)}
        {busy && <div className="ai-msg assistant muted">Working on it…</div>}
        <div ref={end} />
      </div>
      <ErrorBox error={err} />
      <div className="ai-input">
        <textarea rows={2} value={text} placeholder="Describe the report, or what to change…" onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
        <button onClick={() => void send()} disabled={busy || !text.trim()}>Send</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ customize
/** Change a report: list rows (choose columns) or summarize (group by + totals); filters; sort. */
export function SpecEditor({ catalogs, spec, onChange }: { catalogs: Catalogs; spec: Spec; onChange: (s: Spec) => void }) {
  const catalog = catalogs[spec.dataset] ?? catalogs.assessments;
  const groups = useMemo(() => [...new Set(catalog.fields.map((f) => f.group))], [catalog]);
  const fieldOf = useMemo(() => new Map(catalog.fields.map((f) => [f.key, f])), [catalog]);
  const summarize = spec.groupBy.length > 0;
  const set = (p: Partial<Spec>) => onChange({ ...spec, ...p });
  const outKeys = summarize ? [...spec.groupBy, ...spec.measures.map((m, i) => `m${i}_${m.fn}${m.field ? `_${m.field}` : ''}`)] : spec.columns;
  const AGG_LABEL_DS = { ...AGG_LABEL, count: spec.dataset === 'labs' ? 'Number of lab draws' : AGG_LABEL.count };
  const outLabel = (k: string) => fieldOf.get(k)?.label ?? (() => { const i = Number(/^m(\d+)_/.exec(k)?.[1]); const m = spec.measures[i]; return m ? `${AGG_LABEL[m.fn]}${m.field ? ` ${fieldOf.get(m.field)?.label ?? ''}` : ''}` : k; })();
  const numberFields = catalog.fields.filter((f) => f.type === 'number' && f.key !== 'year');   // averages of measurements and results
  return (
    <div className="card report-editor">
      <div className="field" style={{ maxWidth: 360 }}><label htmlFor="rep-ds">Report on</label>
        <select id="rep-ds" value={spec.dataset} onChange={(e) => {
          const ds = e.target.value as Dataset;
          if (ds !== spec.dataset && window.confirm('Switch what the report is about? Its columns and filters start over.')) onChange(blankSpec(ds));
        }}>{(Object.keys(DATASET_LABEL) as Dataset[]).map((d) => <option key={d} value={d}>{DATASET_LABEL[d]}</option>)}</select></div>
      <div className="spread"><h2 style={{ margin: 0 }}>Customize</h2>
        <div className="chips">
          <label className="small"><input type="radio" checked={!summarize} onChange={() => set({ groupBy: [], measures: [], sort: [] })} />List rows</label>
          <label className="small"><input type="radio" checked={summarize} onChange={() => set({ groupBy: ['org'], measures: [{ fn: 'count' }], groupRows: null, sort: [] })} />Summarize (group and total)</label>
        </div></div>

      {!summarize ? (<>
        <fieldset><legend>Sections</legend>
          <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
            <select value={spec.groupRows?.field ?? ''} style={{ maxWidth: 320 }} onChange={(e) => set({ groupRows: e.target.value ? { field: e.target.value } : null })}>
              <option value="">No sections (one list)</option>
              {catalog.fields.filter((f) => f.type !== 'number').map((f) => <option key={f.key} value={f.key}>Section by {f.label}</option>)}
            </select>
            <span className="muted small">Each section gets a heading, then an average row (and count); an overall average ends the report.</span>
          </div>
        </fieldset>
        <fieldset><legend>Columns <span className="muted small">(in this order)</span></legend>
          <div className="chips">{spec.columns.map((k, i) => (
            <span key={k} className="chip">{fieldOf.get(k)?.label ?? k}
              <button type="button" aria-label="Move left" disabled={i === 0} onClick={() => { const c = [...spec.columns]; [c[i - 1], c[i]] = [c[i]!, c[i - 1]!]; set({ columns: c }); }}>‹</button>
              <button type="button" aria-label="Remove" onClick={() => set({ columns: spec.columns.filter((x) => x !== k), sort: spec.sort.filter((s) => s.key !== k) })}>×</button></span>))}</div>
          <div className="report-fields">{groups.map((g) => (
            <div key={g}><div className="muted small"><b>{g}</b></div>
              {catalog.fields.filter((f) => f.group === g).map((f) => (
                <label key={f.key} className="small" title={f.description}><input type="checkbox" checked={spec.columns.includes(f.key)}
                  onChange={(e) => set({ columns: e.target.checked ? [...spec.columns, f.key] : spec.columns.filter((x) => x !== f.key) })} />{f.label}</label>))}
            </div>))}</div>
        </fieldset>
      </>) : (
        <fieldset><legend>Group by and totals</legend>
          <div className="row">
            <div className="field"><label>Group by</label>
              {spec.groupBy.map((k, i) => (
                <div key={i} className="row" style={{ gap: '0.3rem' }}>
                  <select value={k} onChange={(e) => { const g = [...spec.groupBy]; g[i] = e.target.value; set({ groupBy: g, sort: [] }); }}>
                    {catalog.fields.filter((f) => f.type !== 'number' || f.key === 'year' || f.key === 'age').map((f) => <option key={f.key} value={f.key}>{f.group}: {f.label}</option>)}</select>
                  {spec.groupBy.length > 1 && <button type="button" className="link" onClick={() => set({ groupBy: spec.groupBy.filter((_, j) => j !== i), sort: [] })}>×</button>}
                </div>))}
              {spec.groupBy.length < 3 && <button type="button" className="link small" onClick={() => set({ groupBy: [...spec.groupBy, 'month'], sort: [] })}>+ then by…</button>}
            </div>
            <div className="field"><label>Totals</label>
              {spec.measures.map((m, i) => (
                <div key={i} className="row" style={{ gap: '0.3rem' }}>
                  <select value={m.fn} onChange={(e) => { const ms = [...spec.measures]; ms[i] = { fn: e.target.value, field: ['count', 'count_people'].includes(e.target.value) ? undefined : m.field ?? numberFields[0]?.key }; set({ measures: ms, sort: [] }); }}>
                    {catalog.aggregates.map((a) => <option key={a} value={a}>{AGG_LABEL_DS[a as keyof typeof AGG_LABEL_DS] ?? a}</option>)}</select>
                  {!['count', 'count_people'].includes(m.fn) && <select value={m.field ?? ''} onChange={(e) => { const ms = [...spec.measures]; ms[i] = { ...m, field: e.target.value }; set({ measures: ms, sort: [] }); }}>
                    {numberFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}</select>}
                  <button type="button" className="link" onClick={() => set({ measures: spec.measures.filter((_, j) => j !== i), sort: [] })}>×</button>
                </div>))}
              <button type="button" className="link small" onClick={() => set({ measures: [...spec.measures, { fn: 'count' }], sort: [] })}>+ add a total</button>
            </div>
          </div>
        </fieldset>)}

      <fieldset><legend>Filters</legend>
        {spec.filters.map((f, i) => {
          const fd = fieldOf.get(f.field);
          const upd = (p: Partial<Filter>) => { const fs = [...spec.filters]; fs[i] = { ...f, ...p }; set({ filters: fs }); };
          return (
            <div key={i} className="report-filter">
              <select value={f.field} onChange={(e) => { const nf = fieldOf.get(e.target.value)!; upd({ field: nf.key, op: OPS_FOR[nf.type][0]!, value: undefined, value2: undefined }); }}>
                {groups.map((g) => <optgroup key={g} label={g}>{catalog.fields.filter((x) => x.group === g).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</optgroup>)}</select>
              <select value={f.op} onChange={(e) => upd({ op: e.target.value, value: e.target.value === 'last_days' ? 30 : undefined, value2: undefined })}>
                {(fd ? OPS_FOR[fd.type] : []).map((o) => <option key={o} value={o}>{OP_LABEL[o] ?? o}</option>)}</select>
              <div className="report-filter-value">{f.ask ? <span className="muted small">asked when the report is run</span>
                : <ValueInput field={fd} op={f.op} value={f.value} value2={f.value2} onChange={(v, v2) => upd({ value: v, value2: v2 })} />}</div>
              <label className="small" title="Ask for this each time the report is run"><input type="checkbox" checked={!!f.ask} onChange={(e) => upd({ ask: e.target.checked })} />ask</label>
              <button type="button" className="link" onClick={() => set({ filters: spec.filters.filter((_, j) => j !== i) })} aria-label="Remove filter">×</button>
            </div>);
        })}
        <button type="button" className="link small" onClick={() => set({ filters: [...spec.filters, { field: 'org_type', op: 'eq', value: '' }] })}>+ add a filter</button>
      </fieldset>

      <fieldset><legend>Sort</legend>
        {spec.sort.map((s, i) => (
          <div key={i} className="row" style={{ gap: '0.3rem', alignItems: 'center' }}>
            <select value={s.key} onChange={(e) => { const so = [...spec.sort]; so[i] = { ...s, key: e.target.value }; set({ sort: so }); }}>
              {outKeys.map((k) => <option key={k} value={k}>{outLabel(k)}</option>)}</select>
            <select value={s.dir} onChange={(e) => { const so = [...spec.sort]; so[i] = { ...s, dir: e.target.value as 'asc' | 'desc' }; set({ sort: so }); }}>
              <option value="asc">A → Z / low → high</option><option value="desc">Z → A / high → low</option></select>
            <button type="button" className="link" onClick={() => set({ sort: spec.sort.filter((_, j) => j !== i) })}>×</button>
          </div>))}
        {outKeys.length > 0 && spec.sort.length < 3 && <button type="button" className="link small" onClick={() => set({ sort: [...spec.sort, { key: outKeys[0]!, dir: 'asc' }] })}>+ add a sort</button>}
      </fieldset>
      <p className="muted small" style={{ margin: 0 }}>Click <b>Run report</b> to see the changes. Saving keeps the definition (filters with "ask" are asked each time).</p>
    </div>
  );
}

// ------------------------------------------------------------------ PDF print page (rendered by the worker)
export function ReportPrint() {
  const { runId } = useParams();
  const { data, error } = useData<Result & { title: string; requestedAt: string; requestedBy: string | null; dataset?: Dataset }>(`/api/reports/runs/${runId}/data`);
  const catalog = useData<Catalog>(data ? `/api/reports/catalog?dataset=${data.dataset ?? 'assessments'}` : null);
  if (error) return <div data-report-error={error instanceof Error ? error.message : String(error)}><ErrorBox error={error} /></div>;
  if (!data || !catalog.data) return <p className="muted">Loading…</p>;
  const fieldOf = new Map(catalog.data.fields.map((f) => [f.key, f]));
  const wide = data.columns.length > 7;
  return (
    <div data-report-ready="" className={`rptx${wide ? ' wide' : ''}`}>
      <header className="rptx-head">
        <img src="/ngf-logo-print.png" alt="North Greenville Fitness" />
        <div><div className="rptx-title">{data.title}</div>
          <div className="rptx-sub">North Greenville Fitness · {new Date(data.requestedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            {data.requestedBy ? ` · ${data.requestedBy}` : ''} · {data.total.toLocaleString('en-US')} row{data.total === 1 ? '' : 's'}{data.truncated ? ` (first ${data.rows.length.toLocaleString('en-US')})` : ''}</div>
          {data.filters.length > 0 && <div className="rptx-sub">{data.filters.join(' · ')}</div>}</div>
      </header>
      <ResultTable result={data} fieldOf={fieldOf} className="rptx-table" />
      <footer className="rptx-foot">Confidential: contains protected health information. North Greenville Fitness · Travelers Rest, SC</footer>
    </div>
  );
}
